import { type CSSProperties, useMemo } from 'react';

import {
  ArrowSquareOut,
  CalendarBlankIcon,
  CheckCircleIcon,
  Clock,
  DownloadSimple,
  Globe,
  WarningCircleIcon,
} from '@phosphor-icons/react/dist/ssr';
import classNames from 'classnames';

import SimpleModal from '../ui/SimpleModal';

import { redeemOrderModalContentContainer, redeemOrderModalDialogContent } from './RedeemOrderModal.css';
import type { RedeemOrderModalElementProps } from './RedeemOrderModal.type';
import { RedeemOrderModalButton } from './RedeemOrderModalButton';

const WEBINAR_REDEEM_WINDOW_MINUTES = 10;

const isWebinarWithinRedeemWindow = (eventStartsAt?: string): boolean => {
  if (!eventStartsAt) return true;
  const startTime = new Date(eventStartsAt).getTime();
  const now = Date.now();
  return startTime - now <= WEBINAR_REDEEM_WINDOW_MINUTES * 60 * 1000;
};

const formatWebinarDateTime = (eventStartsAt: string, timeZone?: string): string => {
  const date = new Date(eventStartsAt);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZone || undefined,
    timeZoneName: 'short',
  };
  return date.toLocaleString(undefined, options);
};

export function RedeemOrderModalElement(props: RedeemOrderModalElementProps) {
  const {
    element,
    isOpen = false,
    onOpenChange,
    isPreviewing = false,
    translations,
    orderStatusState,
    orderData,
    errorMessage,
    isRedeeming,
    onClickRedeem,
    onAddToCalendar,
    onPostPurchaseContinue,
  } = props;
  const {
    gap,
    width,
    maxWidth,
    padding,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    modalOverlayBackgroundColor,
    modalCloseButtonColor = 'inherit',
    modalCloseButtonPlacementTop,
    modalCloseButtonPlacementRight,
  } = element.attrs || {};

  const generatedStyle = {
    '--overlay-background-color': modalOverlayBackgroundColor,
    '--modal-close-button-color': modalCloseButtonColor,
    '--modal-close-button-placement-top': modalCloseButtonPlacementTop || '16px',
    '--modal-close-button-placement-right': modalCloseButtonPlacementRight || '16px',
  } as CSSProperties;

  const getButtonLabel = (productVariantType: string) => {
    switch (productVariantType) {
      case 'file':
        return translations?.['products.instant_redemption_modal.ready.download_file'];
      case 'link':
        return translations?.['products.instant_redemption_modal.ready.access_link'];
      case 'webinar':
        return translations?.['products.instant_redemption_modal.ready.join_now'];
      case 'coaching_session':
        return translations?.['products.instant_redemption_modal.ready.book_now'];
      default:
        return translations?.['products.instant_redemption_modal.ready.redeem'];
    }
  };

  const getButtonIcon = (productVariantType: string) => {
    switch (productVariantType) {
      case 'file':
        return <DownloadSimple className="h-4 w-4 mr-2" aria-hidden="true" />;
      case 'link':
        return <ArrowSquareOut className="h-4 w-4 mr-2" aria-hidden="true" />;
      case 'webinar':
        return <ArrowSquareOut className="h-4 w-4 mr-2" aria-hidden="true" />;
      case 'coaching_session':
        return <CalendarBlankIcon className="h-4 w-4 mr-2" aria-hidden="true" />;
      default:
        return null;
    }
  };

  const isWebinarOrder = useMemo(
    () => orderData?.order_items.every((item) => item.product_variant.type === 'webinar') ?? false,
    [orderData],
  );

  const canJoinAnyWebinarNow = useMemo(() => {
    if (!isWebinarOrder || !orderData) return false;
    return orderData.order_items.some(
      (item) =>
        item.product_variant.type === 'webinar' &&
        item.fulfillment.can_access &&
        isWebinarWithinRedeemWindow(item.product_variant.event_starts_at),
    );
  }, [isWebinarOrder, orderData]);

  const readyDescriptionKey = useMemo(() => {
    if (!isWebinarOrder) return 'products.instant_redemption_modal.ready.description';
    if (canJoinAnyWebinarNow) return 'products.instant_redemption_modal.ready.webinar_description_join_now';
    return 'products.instant_redemption_modal.ready.webinar_description';
  }, [isWebinarOrder, canJoinAnyWebinarNow]);

  const canRedeemOrderItem = (orderItem: NonNullable<typeof orderData>['order_items'][number]): boolean => {
    if (!orderItem.fulfillment.can_access) return false;
    if (orderItem.product_variant.type === 'webinar') {
      return isWebinarWithinRedeemWindow(orderItem.product_variant.event_starts_at);
    }
    return true;
  };

  const renderedElement = (
    <div
      className={classNames(redeemOrderModalContentContainer, 'flex flex-col h-auto')}
      style={
        {
          '--width': width,
          '--max-width': maxWidth,
          backgroundColor,
          padding,
          borderStyle,
          borderWidth,
          borderRadius,
          borderColor,
          gap,
          '--padding': padding,
        } as CSSProperties
      }
    >
      <div className="text-center">
        {/* Icon */}
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full">
          {orderStatusState === 'ready' && <CheckCircleIcon className="h-8 w-8 text-green-400" aria-hidden="true" />}
          {orderStatusState === 'loading' && (
            <svg
              className="animate-spin w-8 h-8 text-gray-900"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-label={translations?.['shared.actions.loading'] ?? 'Loading'}
            >
              <title>{translations?.['shared.actions.loading'] ?? 'Loading'}</title>
              <circle className="opacity-25" cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={4} fill="none" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {orderStatusState === 'error' && <WarningCircleIcon className="h-8 w-8 text-red-400" aria-hidden="true" />}
        </div>

        {/* Loading state */}
        {orderStatusState === 'loading' && (
          <div className="flex flex-col gap-2 items-center justify-center">
            <h3 className="text-lg font-medium text-gray-900 font-sans">
              {translations?.['products.purchase_complete']}
            </h3>
            <p className="text-sm text-gray-900 font-sans">
              {translations?.['products.instant_redemption_modal.loading.description']}
            </p>
          </div>
        )}

        {/* Error state */}
        {orderStatusState === 'error' && (
          <div className="flex flex-col gap-2 items-center justify-center">
            <p className="text-sm text-gray-900 font-sans">{errorMessage}</p>
          </div>
        )}

        {/* Ready state */}
        {orderStatusState === 'ready' && orderData && (
          <div className="flex flex-col gap-4 items-center justify-center">
            <div className="flex flex-col">
              <h3 className="text-lg font-medium text-gray-900 font-sans">
                {
                  translations?.[
                    isWebinarOrder
                      ? 'products.instant_redemption_modal.ready.webinar_title'
                      : 'products.instant_redemption_modal.ready.title'
                  ]
                }
              </h3>
              <p className="text-sm text-gray-900 font-sans">{translations?.[readyDescriptionKey]}</p>
            </div>
            <div className="flex flex-col gap-3 w-full">
              {orderData.order_items.map((orderItem) => (
                <div key={orderItem.id} className="text-left p-3 border border-gray-300 rounded-md">
                  <div className="flex items-start gap-3">
                    {orderItem.product_variant.thumbnail_url && (
                      <img
                        src={orderItem.product_variant.thumbnail_url}
                        alt=""
                        className="w-12 h-auto object-cover rounded aspect-video"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{orderItem.product_variant.name}</p>
                      <p className="text-xs text-gray-900 line-clamp-4" title={orderItem.product_description}>
                        {orderItem.product_description}
                      </p>
                    </div>
                  </div>
                  {orderItem.product_variant.type === 'webinar' && orderItem.product_variant.event_starts_at && (
                    <div className="mt-2 flex flex-col gap-1 rounded-md bg-gray-100 p-2">
                      <div className="flex items-center gap-1.5 text-xs text-gray-700">
                        <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span>
                          {formatWebinarDateTime(
                            orderItem.product_variant.event_starts_at,
                            orderItem.product_variant.iana_time_zone ?? orderItem.product_variant.time_zone,
                          )}
                        </span>
                      </div>
                      {orderItem.product_variant.time_zone && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          <span>{orderItem.product_variant.time_zone}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex flex-col gap-2">
                    {canRedeemOrderItem(orderItem) && (
                      <RedeemOrderModalButton
                        variant="primary"
                        disabled={isRedeeming}
                        onClick={() => onClickRedeem(orderItem.fulfillment.id)}
                      >
                        {getButtonIcon(orderItem.product_variant.type)}
                        {getButtonLabel(orderItem.product_variant.type)}
                      </RedeemOrderModalButton>
                    )}
                    {orderItem.product_variant.type === 'webinar' &&
                      !isWebinarWithinRedeemWindow(orderItem.product_variant.event_starts_at) && (
                        <p className="text-xs text-gray-500 text-center font-sans">
                          {translations?.['products.instant_redemption_modal.ready.redeem_available_soon'] ??
                            `You can join ${WEBINAR_REDEEM_WINDOW_MINUTES} minutes before the event starts`}
                        </p>
                      )}
                    {orderItem.product_variant.type === 'webinar' && onAddToCalendar && (
                      <RedeemOrderModalButton
                        variant="secondary"
                        onClick={() => onAddToCalendar(orderItem.fulfillment.id)}
                      >
                        <CalendarBlankIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                        {translations?.['products.instant_redemption_modal.ready.add_to_calendar'] ?? 'Add to calendar'}
                      </RedeemOrderModalButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {onPostPurchaseContinue && (
              <RedeemOrderModalButton variant="primary" size="md" onClick={onPostPurchaseContinue}>
                {translations?.['shared.cta.continue'] ?? 'Continue'}
              </RedeemOrderModalButton>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen && !isPreviewing) {
    return null;
  }

  if (isPreviewing) {
    return (
      <div className="w-full h-auto min-h-[600px] flex items-center justify-center relative" style={generatedStyle}>
        <div className="absolute top-0 left-0 w-full h-full bg-[--overlay-background-color]">&nbsp;</div>
        <div className="z-10 relative">{renderedElement}</div>
      </div>
    );
  }

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onOpenChange}
      style={generatedStyle}
      className="shadow-lg"
      showCloseButton
      hideOverflow={false}
      shouldCloseOnOutsideClick={false}
    >
      <div className={classNames(redeemOrderModalDialogContent)}>{renderedElement}</div>
    </SimpleModal>
  );
}
